import { CommunicationsUIStateInbox } from "@/Common/Models/UI/Enums";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { BaseStore } from "@/Common/Stores/BaseStore";
import { RequestMapper, ServerResponseMapper } from "@/Common/Stores/Handlers/Server/RequestMapper";
import { CommunicationsTab_OptimizedV2State } from "@/Communications/Components/CommunicationsTab_OptimizedV2";
import { ExternalMessageInboxState } from "@/Communications/Components/ExternalCommunications/ExternalMessageInboxComponent";
import { BaseExternalMessageFormState } from "@/Communications/Components/ExternalCommunications/Form/BaseExternalMessageFormComponent";
import { MessageTaskEditContainerState } from "@/Communications/Components/MessageTaskEdit/MessageTaskEditContainer";
import { MessageTaskViewContainerState } from "@/Communications/Components/MessageTaskView/MessageTaskViewContainer";
import { RecallEdit_V2State } from "@/Communications/Components/Recalls/RecallEdit_V2";
import { RecallView_V2State } from "@/Communications/Components/Recalls/RecallView_V2";
import { MessageSearch_OptimizedV2State } from "@/Communications/Components/Search/MessageSearch_OptimizedV2";
import { RecallSearch_OptimizedV2State } from "@/Communications/Components/Search/RecallSearch_OptimizedV2";
import { TaskSearch_OptimizedV2State } from "@/Communications/Components/Search/TaskSearch_OptimizedV2";
import { CommunicationsStateModel } from "@/Communications/Models/CommunicationsStateModel";
import { TaskGroupType } from "@/Communications/Models/Tasks/Enums/TaskGroupType";
import { ICommunicationsCountersModel } from "@/Communications/Models/UI/ICommunicationsCounterModel";
import { CommunicationsActions } from "@/Communications/Stores/Actions/CommunicationsActions";
import { CommunicationsHandler } from "@/Communications/Stores/Handlers/Local/CommunicationsHandler";
import { CommunicationsResponseHandler } from "@/Communications/Stores/Handlers/Server/CommunicatonsResponseHandler";
import { PortalCommunicationTableSetting } from "../Models/PortalCommunication/PortalCommunicationTableSetting";

let defaultInstance: CommunicationsStore;

class CommunicationsStore extends BaseStore {
    private communicationsHandler: CommunicationsHandler;
    private serverHandler: CommunicationsResponseHandler;
    private mapper: RequestMapper;
    private serverMapper: ServerResponseMapper;

    private defaultModelCounters = {
        UnreadMessagesCount: 0,
        DraftMessagesCount: 0,
        RecallsCount: 0,
        UnreadTasksCount: 0,
        DueTasksCount: 0,
        TaskDraftsCount: 0
    };

    public CommunicationsCountersModel: ICommunicationsCountersModel;
    public communicationActions: CommunicationsActions;
    public timeoutReferenceGetCount: number;
    public communicationsTabSavedState: CommunicationsStateModel;
    public fetchedSharedItemIds: string[] = [];
    public portalCommunicationTotalCount: number = 0;
    public currentSharedItemId: string;
    public portalCommunicationTableSetting: Record<CommunicationsUIStateInbox, PortalCommunicationTableSetting> = {
        [CommunicationsUIStateInbox.Inbox]: null,
        [CommunicationsUIStateInbox.Sent]: null,
        [CommunicationsUIStateInbox.Drafts]: null,
        [CommunicationsUIStateInbox.Trash]: null,
        [CommunicationsUIStateInbox.Archive]: null,
        [CommunicationsUIStateInbox.FlaggedForReview]: null
    };

    constructor() {
        super();

        var self = this;
        this.timeoutReferenceGetCount = 1;
        this.communicationActions = new CommunicationsActions;
        this.communicationsHandler = new CommunicationsHandler();

        this.serverHandler = new CommunicationsResponseHandler(this.communicationActions);
        this.mapper = new RequestMapper();
        this.serverMapper = new ServerResponseMapper();
        this.serverMapper.AddInstance(this.serverHandler);

        this.mapper.AddInstance(this.communicationsHandler);

        // set default models
        this.CommunicationsCountersModel = Immutable.fromJS(this.defaultModelCounters);

        this.ClientDispatchToken = LocalEventDispatcher.register((request: LocalEvent) => self.mapper.Map(request, self));

        this.ServerDispatchToken = ServerResponseDispatcher.register((response: ServerEvent) => self.serverMapper.Map(response, self));

        this.prepareCommunicationStates();
    }

    public static createSingleton(): CommunicationsStore {
        return this.createStoreSingleton(defaultInstance, () => defaultInstance = new CommunicationsStore());
    }

    public static createSingletonFrom(store: CommunicationsStore, copy: boolean = true): CommunicationsStore {
        return copy
            ? Object.assign(this.createSingleton(), store)
            : this.createStoreSingleton(defaultInstance, () => defaultInstance = store);
    }

    public prepareCommunicationStates(): void {
        if (!this.communicationsTabSavedState) {
            this.communicationsTabSavedState = new CommunicationsStateModel();
            this.communicationsTabSavedState.communicationsTabSavedState = CommunicationsTab_OptimizedV2State.getDefault();
        }
    }

    public GetTaskGroupingTypes(): KeyValuePair<TaskGroupType, string>[] {
        return [
            new KeyValuePair(TaskGroupType.group, cacheStore.GetString("GroupTaskLabel")),
            new KeyValuePair(TaskGroupType.individual, cacheStore.GetString("IndividualTaskLabel"))
        ];
    }

    public saveCommunicationsTabState(communicationsTabState: CommunicationsTab_OptimizedV2State): void {
        this.communicationsTabSavedState.communicationsTabSavedState = communicationsTabState;
    }

    public saveRecallSearchState(recallSearchState: RecallSearch_OptimizedV2State): void {
        this.communicationsTabSavedState.recallSearchSavedState = recallSearchState;
    }

    public saveMessageSearchState(messageSearchState: MessageSearch_OptimizedV2State): void {
        this.communicationsTabSavedState.messageSearchSavedState = messageSearchState;
    }

    public saveTaskSearchState(taskSearchState: TaskSearch_OptimizedV2State): void {
        this.communicationsTabSavedState.taskSearchSavedState = taskSearchState;
    }

    public saveRecallEditState(recallEditState: RecallEdit_V2State): void {
        this.communicationsTabSavedState.recallEditSavedState = recallEditState;
    }

    public saveRecallViewState(recallViewState: RecallView_V2State): void {
        this.communicationsTabSavedState.recallViewSavedState = recallViewState;
    }

    public saveMessageTaskViewContainerState(messageTaskViewContainerState: MessageTaskViewContainerState): void {
        this.communicationsTabSavedState.messageTaskViewContainerSavedState = messageTaskViewContainerState;
    }

    public saveMessageTaskEditContainerState(messageTaskEditContainerState: MessageTaskEditContainerState): void {
        this.communicationsTabSavedState.messageTaskEditContainerSavedState = messageTaskEditContainerState;
    }

    public saveExternalMessageFormState(externalMessageFormState: BaseExternalMessageFormState): void {
        this.communicationsTabSavedState.externalMessageFormState = externalMessageFormState;
    }

    public clearExternalMessageFormState(): void {
        this.communicationsTabSavedState.externalMessageFormState = null;
    }

    public saveExternalMessageInboxSearchState(externalMessageSearchSettingsState: ExternalMessageInboxState): void {
        this.communicationsTabSavedState.externalMessageInboxSearchSettingsState = externalMessageSearchSettingsState;
    }
}

export { CommunicationsStore, defaultInstance as communicationsStore };
